/**
 * External dependencies
 */
import { withDispatch, withSelect } from '@wordpress/data';
import { compose } from '@wordpress/compose';
import { Component, Fragment } from '@wordpress/element';
import { PropTypes } from 'prop-types';
import { __ } from '@wordpress/i18n';
import moment from 'moment';

/**
 * Internal dependencies
 */
import EventItem from '../../components/event-item';
import EmptyEventItem from '../../components/event-item/empty';
import {
	getDateQueryLimits,
	isCustom,
	getNoSlotsInRangeMessage,
	getErrorLoadingSlotsInRangeMessage,
} from '../../includes/date-utils';
import { DEFAULT_PAGE_SIZE } from '../../includes/misc-utils';
import PageLoader from '../../components/page-loader';

class EventTable extends Component {
	constructor( props ) {
		super( props );
		this.state = {
			pendingLoadMore: false,
		};
	}

	loadMoreSlots = () => {
		const {
			availabilityQuery,
			nextAvailabilityPage,
			isLoading,
		} = this.props;

		if ( isLoading ) {
			this.setState( { pendingLoadMore: true } );
		} else {
			nextAvailabilityPage( availabilityQuery );
		}
	}

	// We relly on props change to have this triggered.
	// After availability stops loading we should have isLoading(true) -> false;
	maybeLoadMoreSlots() {
		const { pendingLoadMore } = this.state;
		if ( pendingLoadMore ) {
			this.loadMoreSlots();
		}
	}

	reloadWindow = () => {
		window.location.reload();
	}

	render() {
		const { isError, dateRangeType } = this.props;
		this.maybeLoadMoreSlots();

		return isError ?
			(
				<div className="woocommerce-error notice notice-error">
					{ getErrorLoadingSlotsInRangeMessage( dateRangeType ) }
					{ ' ' }
					<a href="#" onClick={ this.reloadWindow }>{ __( 'Try again', 'woocommerce-bookings-availability' ) }</a>{ /* eslint-disable-line jsx-a11y/anchor-is-valid */ }
				</div>
			) : (
				this.renderEvents()
			);
	}

	renderEvents() {
		const {
			availability,
			isPreview,
			isRequesting,
			products,
			infiniteScrollEnabled,
			remainingRecords,
			isLastPage,
			slotsPerPage,
			dateRangeType,
			availabilityQuery: { minDate, maxDate, page },
		} = this.props;

		let previousDate = null;
		let shownAvailability = availability;
		const placeholderAvailability = availability.slice( 0 );
		const eventItems = [];

		const placeHolderCount = ( infiniteScrollEnabled || 0 === remainingRecords ) ? DEFAULT_PAGE_SIZE : Math.min( slotsPerPage, remainingRecords );
		const placeHolderProduct = {
			name: 'placeholder',
			duration: 1,
			duration_unit: 'day',

		};
		if ( isRequesting ) {
			for ( let i = 0; i < placeHolderCount; i++ ) {
				placeholderAvailability.push( {
					title: 'placeholder',
					date: new Date( minDate ),
					cost: 0.00,
					productId: 0,
					available: 0,
					placeholder: true,
				} );
			}
			shownAvailability = placeholderAvailability;
		}

		let key = 0;
		shownAvailability.forEach( ( data ) => {
			const { date } = data;
			const product = products[ data.productId ] || placeHolderProduct;
			const momentPreviousDate = moment( previousDate );
			let addSubHeader = false;
			if ( ! previousDate || momentPreviousDate.isBefore( date, 'day' ) ) {
				momentPreviousDate.add( 1, 'day' );

				while ( momentPreviousDate.isBefore( date, 'day' ) ) {
					eventItems.push( <EmptyEventItem key={ key } date={ momentPreviousDate.toDate() } /> );
					key++;
					momentPreviousDate.add( 1, 'day' );
				}
				addSubHeader = true;
			}

			previousDate = date;
			eventItems.push(
				<EventItem isPreview={ isPreview } key={ key } data={ data } product={ product } addSubheader={ addSubHeader } />
			);
			key++;
		} );

		const addEndPaddingItems = ( ! ( page === 1 && isRequesting ) ) && remainingRecords === 0;
		if ( addEndPaddingItems && moment( previousDate ).isBefore( maxDate, 'day' ) ) {
			const momentPreviousDate = moment( previousDate );
			while ( momentPreviousDate.isBefore( maxDate, 'day' ) ) {
				momentPreviousDate.add( 1, 'day' );
				eventItems.push( <EmptyEventItem key={ key } date={ momentPreviousDate.toDate() } /> );
				key++;
			}
		}
		return (
			shownAvailability.length ? (
				<Fragment>
					<ul className="wc-bookings-availability-event-table">
						{ eventItems }
					</ul>
					{ ! isLastPage &&
						<PageLoader infiniteScrollEnabled={ infiniteScrollEnabled } remainingRecords={ remainingRecords } loadMore={ this.loadMoreSlots } />
					}
				</Fragment>
			) : (
				<div className="woocommerce-error notice notice-empty">
					{ getNoSlotsInRangeMessage( dateRangeType ) }
				</div>
			)
		);
	}
}

EventTable.defaultProps = {
	productIds: [],
	categoryIds: [],
	offset: 0,
};

EventTable.propTypes = {
	/**
	 * Array of availability data in use by EventItem.
	 */
	availability: PropTypes.array.isRequired,
	/**
	 * Sets up component for in-editor preview if true.
	 */
	isPreview: PropTypes.bool,
	/**
	 * Whether the request is still processing.
	 */
	isRequesting: PropTypes.bool,
	/**
	 * Object indexed by product id with product details.
	 */
	products: PropTypes.oneOfType( [
		PropTypes.object.isRequired,
		PropTypes.array.isRequired,
	] ),

	/**
	 * If we should automattically load the next page on scroll.
	 */
	infiniteScrollEnabled: PropTypes.bool.isRequired,
	/**
	 * Number of records that have not been displayed.
	 */
	remainingRecords: PropTypes.number.isRequired,
	/**
	 * If we have loaded the last page and there are no more pages.
	 */
	isLastPage: PropTypes.bool,
	/**
	 * Page size.
	 */
	slotsPerPage: PropTypes.number,
	/**
	 * One of DATE_RANGES day, week or month.
	 */
	dateRangeType: PropTypes.string,
	/**
	 * Query object for api request.
	 */
	availabilityQuery: PropTypes.object.isRequired,
	/**
	 * Callback to load next page.
	 */
	nextAvailabilityPage: PropTypes.func,
	/**
	 * If an error happened in the api.
	 */
	isError: PropTypes.bool,
	/**
	 * Whether the list of items is still loading.
	 */
	isLoading: PropTypes.bool,
};

export default compose(
	withSelect( ( select, props ) => {
		const {
			getAvailability,
			getAvailabilityTotalRecords,
			getPage,
			isGetAvailabilityError,
			isGetAvailabilityRequesting,
			getProducts,
			isGetProductsRequesting,
			isGetProductsError,
		} = select( 'woocommerce-bookings-availability' );
		const {
			productIds,
			categoryIds,
			resourceIds,
			dateRangeType,
			offset,
			infiniteScrollEnabled,
			slotsPerPage,
		} = props;

		let currentDate = 1;
		let { minDate, maxDate } = props;

		if ( ! isCustom( dateRangeType ) ) {
			if ( 0 === offset && 'this-month' === dateRangeType ) {
				const date = new Date();
				currentDate = date.getDate();
			}

			const dateRange = getDateQueryLimits( dateRangeType, offset, currentDate );
			minDate = dateRange.minDate;
			maxDate = dateRange.maxDate;
		}

		const availabilityQuery = {
			minDate,
			maxDate,
			productIds,
			categoryIds,
			resourceIds,
		};

		const productsQuery = {
			productIds,
		};

		if ( ! infiniteScrollEnabled ) {
			availabilityQuery.pageSize = slotsPerPage;
		}

		const page = getPage( availabilityQuery );
		availabilityQuery.page = page;

		const products = getProducts( productsQuery );
		const availability = getAvailability( availabilityQuery );
		const availabilityRecordsCount = getAvailabilityTotalRecords( availabilityQuery );
		const isError = isGetAvailabilityError( availabilityQuery ) || isGetProductsError();
		const isRequesting = isGetAvailabilityRequesting( availabilityQuery ) || isGetProductsRequesting();
		const remainingRecords = availabilityRecordsCount - availability.length;
		const isLastPage = 0 === remainingRecords;

		return { availability, isError, isRequesting, availabilityQuery, isLastPage, products, remainingRecords };
	} ),
	withDispatch( ( dispatch ) => ( {
		nextAvailabilityPage: dispatch( 'woocommerce-bookings-availability' ).nextAvailabilityPage,
		resetPageNumber: dispatch( 'woocommerce-bookings-availability' ).resetPageNumber,
	} ) ),
)( EventTable );

// export lower order component to expose it for unit testing.
export { EventTable };
